import { useEffect } from 'react';
import useSWR from 'swr';
import config from '@packages/config';
import { useCustomer } from '@packages/utilities';

export type AgnitasResponse = {
  /** Suscription conformation is pending */
  isPending?: boolean;
  /** Subscription is active */
  hasPermission?: boolean;
};

export type AgnitasStatus = AgnitasResponse & {
  email?: string;
};

const sessionKey = 'newsletterStatus';

/**
 * This Hook provides you with agnitas status data for the currently logged in user.
 *
 * It stores the result in the browser's sessionStorage to avoid another api call after e.g.
 * a page reload.
 *
 * @returns User's email and if the newsletter subscription is active or pending
 */
export const useAgnitasData = (
  callback?: (result: AgnitasStatus) => void,
): AgnitasStatus | null => {
  const apiAgnitasUrl = config.forms.apiAgnitasUrl.values.url;

  const { customer, isLoading } = useCustomer();
  const email = customer?.email;

  // the key for useSWR. We can only use it once the email from user is present
  const key =
    email &&
    `${apiAgnitasUrl}/recipientInformation?${new URLSearchParams({
      emailAddress: email,
      areaKey: 'doiPendingLayer',
    })}`;

  /**
   * remove SessionStorage on session change (login / logout)
   */
  useEffect(() => {
    if (!email && !isLoading) {
      sessionStorage.removeItem(sessionKey);
    }
  }, [email, isLoading]);

  const { data, error } = useSWR(
    key,
    async (url) => {
      // try to get previously fetched Data from sessionStorage
      const sessionStorageData = sessionStorage.getItem(sessionKey);

      // if there was data, return parsed object. Otherwise fetch new data
      return sessionStorageData
        ? JSON.parse(sessionStorageData)
        : fetch(url).then((r) => {
            if (r.ok) {
              return r.json();
            }
            throw new Error();
          });
    },
    {
      onSuccess: async (response: AgnitasResponse) => {
        // after fetching, set data to sessionStorage for future use
        sessionStorage.setItem(sessionKey, JSON.stringify(response));
        if (callback) {
          callback({ ...response, email });
        }
      },
      onError: () => {
        // clear session storage if there was an error
        sessionStorage.removeItem(sessionKey);
      },
    },
  );

  if (error) {
    return { email, hasPermission: false, isPending: false };
  }

  // return the result object once we have agnitasData and the user's email
  return data && email
    ? {
        ...data,
        email: customer?.email,
      }
    : null;
};
